import { useState, useEffect, useMemo, useRef } from 'react';
import { useTestStore, type TestId } from '@/store/useTestStore';
import { t } from '@/i18n/translations';
import { TestCard } from '@/components/TestCard';
import { MetricBox } from '@/components/MetricBox';
import { fmtInt, now, clamp } from '@/utils/format';
import { useFpsMeter } from '@/hooks/useFpsMeter';
import { qualityFromFps, scoreFromFps } from '@/utils/scoring';

interface Props { autoRun: boolean; autoNonce: number; }

export function CssAnimTest({ autoRun, autoNonce }: Props) {
  const { language, reportResult, onAutoDone } = useTestStore();
  const [running, setRunning] = useState(false);
  const [count, setCount] = useState(180);
  const fps = useFpsMeter(running);
  const parallaxRef = useRef<HTMLDivElement>(null);
  const raf = useRef(0);
  const t0 = useRef(0);
  const autoDoneRef = useRef(false);

  function start() {
    autoDoneRef.current = false;
    setRunning(true);
    t0.current = now();
    function loop(time: number) {
      const dt = time - t0.current;
      const x = Math.sin(dt / 1100) * 26;
      const y = Math.cos(dt / 1600) * 18;
      if (parallaxRef.current) {
        parallaxRef.current.style.transform = `translate3d(${x}px,${y}px,0)`;
      }
      raf.current = requestAnimationFrame(loop);
    }
    raf.current = requestAnimationFrame(loop);
  }

  function stop() {
    setRunning(false);
    cancelAnimationFrame(raf.current);
    const sc = Math.round(scoreFromFps(fps));
    const testId: TestId = 'tv-css';
    reportResult(testId, { score: sc, primary: `FPS ${fps.toFixed(1)}`, details: { fps } });
    if (autoRun && !autoDoneRef.current) {
      autoDoneRef.current = true;
      onAutoDone(testId);
    }
  }

  function reset() {
    stop();
    setCount(180);
  }

  useEffect(() => {
    if (!autoRun) return;
    setCount(180);
    start();
    const timer = setTimeout(() => stop(), 6000);
    return () => clearTimeout(timer);
  }, [autoRun, autoNonce]);

  const q = qualityFromFps(fps);

  const boxes = useMemo(() => {
    const n = clamp(parseInt(String(count), 10) || 180, 60, 420);
    const out = [];
    for (let i = 0; i < n; i++) {
      out.push({
        i,
        left: (i * 47) % 92 + 4,
        top: (i * 29) % 88 + 6,
        delay: (i % 40) * -0.07,
      });
    }
    return out;
  }, [count]);

  return (
    <TestCard title={t('tv.css.title', language)} description={t('tv.css.desc', language)}>
      <div className="flex flex-wrap gap-2.5 items-center">
        <button className="btn btn-primary" onClick={start} disabled={running || autoRun}>{t('btn.start', language)}</button>
        <button className="btn btn-danger" onClick={stop} disabled={!running || autoRun}>{t('btn.stop', language)}</button>
        <button className="btn" onClick={reset} disabled={autoRun}>{t('btn.reset', language)}</button>
        <div className="flex-1" />
        <div className="field">
          <div className="label">{t('label.elements', language)}</div>
          <input value={count} disabled={running || autoRun} onChange={(e) => setCount(Number(e.target.value))} inputMode="numeric" className="input w-24" />
        </div>
      </div>

      <div className="grid grid-cols-4 gap-2.5 mt-3 max-lg:grid-cols-2 max-sm:grid-cols-1">
        <MetricBox label={t('metric.fps', language)} value={running ? fps.toFixed(1) : '—'} hint={t('hint.fpsGood', language)} />
        <MetricBox label={t('metric.quality', language)} value={running ? q.label : '—'} />
        <MetricBox label="Compositor" value="transform/opacity" />
        <MetricBox label={t('label.elements', language)} value={fmtInt(boxes.length)} />
      </div>

      <div className="h-px bg-border my-3.5" />

      <div className="h-[56vh] min-h-[340px] border border-border rounded-2xl overflow-hidden relative bg-[radial-gradient(600px_240px_at_20%_20%,rgba(100,181,255,0.18),transparent_60%),radial-gradient(600px_240px_at_80%_60%,rgba(31,191,117,0.14),transparent_60%),rgba(0,0,0,0.18)]">
        <div
          ref={parallaxRef}
          className="absolute inset-0 pointer-events-none opacity-30"
          style={{
            background: 'repeating-linear-gradient(90deg,rgba(255,255,255,.06) 0 1px,transparent 1px 120px),repeating-linear-gradient(0deg,rgba(255,255,255,.05) 0 1px,transparent 1px 90px)',
            willChange: 'transform',
          }}
        />
        {running ? (
          boxes.map((b) => (
            <div
              key={b.i}
              className="anim-box absolute w-[46px] h-[46px] rounded-xl bg-gradient-to-br from-accent/90 to-good/75 shadow-lg"
              style={{
                left: b.left + '%',
                top: b.top + '%',
                animationDelay: b.delay + 's',
                willChange: 'transform, opacity',
              }}
            />
          ))
        ) : (
          <div className="text-xs text-muted p-3">{autoRun ? t('status.autoRun', language) : t('status.pressStart', language)}</div>
        )}
      </div>
    </TestCard>
  );
}
